import {
  argsKey,
  cache,
  ErrorBoundaryContext,
  type Fetcher,
} from "@prismicio/editor-support/Suspense"
import React, { type ReactNode, useMemo } from "react"

/* If you use one of the Suspense functions like useRequest, you WANT to also use this. */

export interface ErrorBoundaryProps {
  /* The default children when no error has been caught. */
  children?: ReactNode
  /* The node to render when an error has been caught. */
  renderError?: (error: unknown, reset: () => void) => ReactNode
  /* When the reset condition is manual, use this to know about the newest error. */
  onError?: (error: unknown) => void
  /* Additional reset handler */
  onReset?: () => void
}

interface State {
  error?: unknown
  handleError?: boolean
  requests: Map<Fetcher, Map<string, readonly unknown[]>>
}

type ErrorInterceptor = (error: unknown, resetBoundary: () => void) => boolean

/* Whether a given error should be directly handled by this component (return false) or by another part of the App (return true). */
let globalErrorInterceptor: ErrorInterceptor = () => false

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { requests: new Map() }
  }

  static registerGlobalErrorInterceptor(value: ErrorInterceptor) {
    globalErrorInterceptor = value
  }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  override componentDidCatch(error: unknown): void {
    if (import.meta.env.DEV) console.warn(error)

    const handleError = !globalErrorInterceptor(error, this.reset)
    this.setState({ error, handleError })

    // Notify our parent, but async as componentDidCatch is a render phase hook
    // and it's forbidden for our parent to update its state while this is already rendering.
    if (handleError) setTimeout(() => this.props.onError?.(error))
  }

  private registerRequest = (fetcher: Fetcher, args: readonly unknown[]) => {
    const key = argsKey(fetcher, args)
    const map = this.state.requests
    if (!map.has(fetcher)) map.set(fetcher, new Map())
    map.get(fetcher)?.set(key, args)
  }

  reset = () => {
    // Remove all the suspense requests that were handled by this boundary from the cache.
    // This means the children's requests will not hit the cache and will have a chance to succeed.
    this.state.requests.forEach((keys, fetcher) => {
      // "any" because the Map can contain any combination of Fetchers and their args.
      // There's no way to make this safe in any meaningful way.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      keys.forEach((args) => cache.clear(fetcher as Fetcher<any>, args as any))
    })

    this.props.onReset?.()

    this.setState({
      error: undefined,
      handleError: undefined,
      requests: new Map(),
    })
  }

  override render() {
    return (
      <Provider reset={this.reset} registerRequest={this.registerRequest}>
        {this.renderChildren()}
      </Provider>
    )
  }

  private renderChildren = () => {
    const { children, renderError = () => null } = this.props
    const { error, handleError } = this.state

    if (!error) return children
    if (handleError) return renderError(error, this.reset)
    return null
  }
}

interface ProviderProps {
  children: ReactNode
  registerRequest: (fetcher: Fetcher, args: readonly unknown[]) => void
  reset: () => void
}

function Provider(props: ProviderProps) {
  const { children, reset, registerRequest } = props

  const value = useMemo(
    () => ({ reset, registerRequest }),
    [reset, registerRequest],
  )

  return (
    <ErrorBoundaryContext.Provider value={value}>
      {children}
    </ErrorBoundaryContext.Provider>
  )
}
